// Self-service lead-window guard + visit/sync helpers. Framework-free so mobile (WF14) reuses the
// exact same logic. Lead minutes ALWAYS come from the API scheduling-config (never hard-coded) so
// Command changes take effect immediately (WF10 known risk: window-config drift).
import type { AppointmentView, SchedulingConfig } from "@forcebase-ai/schema/patient";

export interface LeadWindow {
  /** True while the member can still self-serve (now ≤ start − lead). */
  allowed: boolean;
  /** The cutoff instant after which self-service is blocked. */
  cutoff: Date;
}

export function leadWindow(
  scheduledAtIso: string,
  leadMinutes: number,
  now = Date.now(),
): LeadWindow {
  const cutoffMs = new Date(scheduledAtIso).getTime() - leadMinutes * 60_000;
  return { allowed: now <= cutoffMs, cutoff: new Date(cutoffMs) };
}

export function canSelfReschedule(
  appt: Pick<AppointmentView, "scheduled_at" | "status">,
  cfg: Pick<SchedulingConfig, "reschedule_min_lead_minutes">,
  now = Date.now(),
): boolean {
  if (appt.status === "cancelled" || appt.status === "completed") return false;
  return leadWindow(appt.scheduled_at, cfg.reschedule_min_lead_minutes, now).allowed;
}

export function canSelfCancel(
  appt: Pick<AppointmentView, "scheduled_at" | "status">,
  cfg: Pick<SchedulingConfig, "cancel_min_lead_minutes">,
  now = Date.now(),
): boolean {
  if (appt.status === "cancelled" || appt.status === "completed") return false;
  return leadWindow(appt.scheduled_at, cfg.cancel_min_lead_minutes, now).allowed;
}

const OPEN_BEFORE_MS = 10 * 60 * 1000;
const CLOSE_AFTER_MS = 15 * 60 * 1000;

/** Join is open from 10 min before start to 15 min after the visit ends (mirrors the API window). */
export function withinJoinWindow(
  appt: Pick<AppointmentView, "scheduled_at" | "duration_minutes" | "status">,
  now = Date.now(),
): boolean {
  if (appt.status === "cancelled") return false;
  const start = new Date(appt.scheduled_at).getTime();
  const end = start + (appt.duration_minutes || 20) * 60_000;
  return now >= start - OPEN_BEFORE_MS && now <= end + CLOSE_AFTER_MS;
}

export type SyncTone = "ok" | "pending" | "failed";

/** Collapses the appointment's Zoom/EMR propagation state into a single member-facing badge. The
 * patient is never blocked by a partial-failed leg — it surfaces as "syncing", not an error. */
export function syncSummary(appt: Pick<AppointmentView, "emr_sync_status">): {
  tone: SyncTone;
  label: string;
} {
  switch (appt.emr_sync_status) {
    case "synced":
      return { tone: "ok", label: "Confirmed" };
    case "failed":
      return { tone: "pending", label: "Syncing…" }; // never shown as an error to the member
    default:
      return { tone: "pending", label: "Syncing…" };
  }
}
